import React, { memo, useMemo } from 'react';

import { Badge as UCLBadge } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { Tabs } from 'expo-router';
import { useAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { Box, BoxProps, Typography } from 'components/ui';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import {
  TAB_ICONS,
  getTabTestId,
  tabAnalyticsListeners,
  tabBarStyles,
} from 'navigation/layouts/tabs-layout-shared';
import { getTabBarItemSafeAreaStyle, getTabBarSafeAreaStyle } from 'navigation/tab-bar-safe-area';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LANGUAGES, TranslationKey } from 'state/intl/types';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { primitive, spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isAndroid, isWeb } from 'utils/environment';

type NavLabelKey = Extract<
  TranslationKey,
  'navHome' | 'navMenu' | 'navMyCode' | 'navOffers' | 'navRewards'
>;

function ClassicTabsLayout() {
  // English cold starts already paint correct labels from the static en catalog.
  // Only remount Tabs after async catalogs load for languages where those labels
  // change (e.g. French); otherwise Expo Router would discard early tab state.
  const { locale, language, messagesLoaded } = useLocale();
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();
  const tabsKey = language === LANGUAGES.en ? 'tabs-en' : messagesLoaded ? locale : 'i18n-pending';
  const tabBarStyle = useMemo(
    () => [
      tabBarStyles.tabBarStyle,
      getTabBarSafeAreaStyle({
        isAndroidDevice: isAndroid,
        safeAreaBottomInset,
      }),
    ],
    [safeAreaBottomInset]
  );
  const tabBarItemStyle = useMemo(
    () =>
      getTabBarItemSafeAreaStyle({
        isAndroidDevice: isAndroid,
      }),
    []
  );

  const screenOptions = useMemo(
    () => ({
      lazy: true,
      header: () => <StandardMobileHeader />,
      tabBarButton: NativeTouchFeedbackTabBarIcon,
      tabBarItemStyle,
      tabBarStyle,
      tabBarLabelPosition: 'below-icon' as const,
      sceneStyle: { backgroundColor: primitive.$mayoEggWhite },
    }),
    [tabBarItemStyle, tabBarStyle]
  );

  const navHomeText = t('navHome');
  const navMenuText = t('navMenu');
  const navMyCodeText = t('navMyCode');
  const navOffersText = t('navOffers');
  const navRewardsText = t('navRewards');

  return (
    <Tabs key={tabsKey} screenOptions={screenOptions}>
      <Tabs.Screen
        name="index"
        listeners={tabAnalyticsListeners.index}
        options={useMemo(
          () => ({
            header: () => <StandardMobileHeader hideTsm={!isDesktopWeb} />,
            title: navHomeText,
            tabBarButtonTestID: getTabTestId('Home'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navHome" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="home" focused={focused} />
            ),
          }),
          [isDesktopWeb, navHomeText]
        )}
      />
      <Tabs.Screen
        name="menu"
        listeners={tabAnalyticsListeners.menu}
        options={useMemo(
          () => ({
            title: navMenuText,
            tabBarButtonTestID: getTabTestId('Menu'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navMenu" focused={focused} />
            ),
            headerShown: false,
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="menu" focused={focused} />
            ),
          }),
          [navMenuText]
        )}
      />
      <Tabs.Screen
        name="my-code"
        listeners={tabAnalyticsListeners['my-code']}
        options={useMemo(
          () => ({
            title: navMyCodeText,
            tabBarButtonTestID: getTabTestId('My-Code'),
            tabBarItemStyle: [tabBarItemStyle, { display: isDesktopWeb ? 'none' : 'flex' }],
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navMyCode" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="myCode" focused={focused} BadgeComponent={MyCodeBadge} />
            ),
          }),
          [isDesktopWeb, navMyCodeText, tabBarItemStyle]
        )}
      />
      <Tabs.Screen
        name="offers"
        listeners={tabAnalyticsListeners.offers}
        options={useMemo(
          () => ({
            title: navOffersText,
            tabBarButtonTestID: getTabTestId('Offers'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navOffers" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="offers" focused={focused} />
            ),
            headerShown: false,
          }),
          [navOffersText]
        )}
      />
      <Tabs.Screen
        name="rewards"
        listeners={tabAnalyticsListeners.rewards}
        options={useMemo(
          () => ({
            title: navRewardsText,
            tabBarButtonTestID: getTabTestId('Rewards'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navRewards" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="rewards" focused={focused} />
            ),
          }),
          [navRewardsText]
        )}
      />
    </Tabs>
  );
}

export default memo(ClassicTabsLayout);

// this doesn't work on web
const NativeTouchFeedbackTabBarIcon = !isWeb
  ? ({ key, ...props }: any) => <TouchableOpacity key={key} activeOpacity={0.3} {...props} />
  : undefined;

type TabBarIconProps = {
  name: keyof typeof TAB_ICONS;
  focused: boolean;
  BadgeComponent?: React.ComponentType | undefined;
};

const TabBarIcon: React.FC<TabBarIconProps> = memo(({ name, focused, BadgeComponent }) => {
  const appTakeover = useAppTakeover();
  const appTakeoverMenuIcon =
    name === 'menu'
      ? focused
        ? appTakeover?.menuIconActive ?? appTakeover?.menuIcon
        : appTakeover?.menuIcon
      : undefined;

  return (
    <View style={tabBarStyles.iconWrapper}>
      <View
        style={[tabBarStyles.activeIndicatorContainer, focused && tabBarStyles.activeIndicator]}
      />
      <Image
        alt=""
        aria-hidden={true}
        source={appTakeoverMenuIcon || (focused ? TAB_ICONS[name].active : TAB_ICONS[name].base)}
        style={tabBarStyles.iconStyles}
        {...hiddenAccessibilityPlatformProps}
      />
      {BadgeComponent ? <BadgeComponent /> : null}
    </View>
  );
});

const TabLabel: React.FC<{ textKey: NavLabelKey; focused: boolean }> = memo(
  ({ textKey, focused }) => {
    // Subscribe so English (no Tabs remount) re-reads `t()` after catalog sync.
    const { language, messagesLoaded } = useLocale();
    const text = t(textKey);
    // Keep messagesLoaded in the render graph for non-English before Tabs remounts.
    const labelKey =
      language === LANGUAGES.en ? textKey : `${textKey}-${messagesLoaded ? 'ready' : 'pending'}`;

    return (
      <Typography
        key={labelKey}
        style={focused ? tabBarStyles.focusedLabelStyle : tabBarStyles.unfocusedLabelStyle}
        adjustsFontSizeToFit
        numberOfLines={2}
      >
        {text}
      </Typography>
    );
  }
);

const MyCodeBadge = memo(() => {
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const count = inRestaurantRedemptionCart.reduce(
    (acc, currentValue) => acc + currentValue.quantity,
    0
  );

  if (!count) {
    return null;
  }

  return (
    <BadgeOuter>
      <BadgeInner variant="number-solid">{count.toString()}</BadgeInner>
    </BadgeOuter>
  );
});

const BadgeOuter = ({ style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.badgeOuter, style]} />
);

const BadgeInner = UCLBadge.withConfig({
  bgColor: primitive.$meltyYellow,
  _text: {
    fontFamily: 'heading',
    fontSize: 'xs',
    color: primitive.$bbqBrown,
  },
  width: '$5',
  height: '$5',
  style: {
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
});

const styles = StyleSheet.create({
  badgeOuter: {
    position: 'absolute',
    right: spacing.$5,
    top: -spacing.$5,
    zIndex: 1,
  },
});
